import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTour } from '../tour/TourContext';
import Logo from './Logo';

// Navbar — appears on all protected pages
// Props: user — the logged in user object
// Wide screens: links across the bar. Below 1240px: a menu button (top left) opens a side panel.
export default function Navbar({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const tour = useTour();
  const [open, setOpen] = useState(false);

  // Close the menu after navigating
  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  // Nav links — visible to all logged in users
  const links = [
    { path: '/dashboard', label: 'Dashboard' },
    { path: '/accounts', label: 'Accounts' },
    { path: '/manage', label: 'Manage' },
    { path: '/customer-value', label: 'Customer Value' },
    // Admin only
    ...(user?.role === 'admin' ? [
      { path: '/tenants', label: 'Tenants' },
      { path: '/inventory', label: 'Inventory' },
      { path: '/ledger', label: 'Ledger' },
      { path: '/messaging', label: 'Messaging' },
      { path: '/audit', label: 'Audit' },
    ] : []),
  ];
  const isActive = (p) => location.pathname === p;

  return (
    <nav style={{
      background: "#ffffff",
      borderBottom: "1px solid #dbe3ec",
      boxShadow: "0 1px 8px rgba(15,23,42,0.06)",
      padding: "0 24px",
      position: "sticky",
      top: 0,
      zIndex: 100
    }} className="mw-navbar">
      <style>{`
        .mw-nav-toggle { display: none }
        .mw-nav-link:hover { color: #1a5fa4 !important }
        .mw-nav-link { white-space: nowrap }
        @media (max-width: 1480px) {
          .mw-nav-whoami { display: none !important }
          .mw-nav-link { padding: 9px 10px !important; font-size: 15px !important }
        }
        @media (max-width: 1240px) {
          .mw-nav-links, .mw-nav-user { display: none !important }
          .mw-nav-toggle { display: inline-flex !important }
          .mw-nav-bar { justify-content: flex-start !important }
        }
        @media (max-width: 640px) {
          .mw-navbar { padding: 0 12px !important }
          .mw-nav-bar { height: 60px !important }
        }
        @keyframes mwDrawerIn { from { transform: translateX(-100%) } to { transform: none } }
        @keyframes mwShadeIn { from { opacity: 0 } to { opacity: 1 } }
        @media (prefers-reduced-motion: reduce) { .mw-drawer, .mw-shade { animation: none !important } }
      `}</style>
      <div className="mw-nav-bar" style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: 72, gap: 12 }}>

        {/* Menu button (narrow screens) — top left */}
        <button className="mw-nav-toggle" onClick={() => setOpen(true)} aria-expanded={open} aria-controls="mw-app-menu" aria-label="Open menu"
          style={{ alignItems: "center", justifyContent: "center", width: 44, height: 44, background: "#ffffff", border: "1px solid #cbd5e1", borderRadius: 10, color: "#0f172a", cursor: "pointer", flexShrink: 0 }}>
          <svg width="20" height="16" viewBox="0 0 20 16" aria-hidden="true">
            <path d="M1 1.5h18M1 8h18M1 14.5h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {/* Logo */}
        <button onClick={() => navigate('/dashboard')} aria-label="Smart Maji — dashboard"
          style={{ display: "flex", alignItems: "center", background: "none", border: "none", padding: 0, cursor: "pointer", minWidth: 0 }}>
          <Logo height={34} />
        </button>

        {/* Nav links (wide screens) */}
        <div data-tour="nav" className="mw-nav-links" style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {links.map(link => (
            <button key={link.path} className="mw-nav-link" onClick={() => navigate(link.path)}
              aria-current={isActive(link.path) ? 'page' : undefined}
              style={{
                background: isActive(link.path) ? "#e7f0fa" : "none", border: "none", borderRadius: 8,
                padding: "9px 14px", color: isActive(link.path) ? "#1a5fa4" : "#334155",
                fontSize: 15.5, fontWeight: isActive(link.path) ? 700 : 500, cursor: "pointer", transition: "color 0.2s",
              }}>
              {link.label}
            </button>
          ))}
        </div>

        {/* User info + logout (wide screens) */}
        <div data-tour="user-menu" className="mw-nav-user" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div className="mw-nav-whoami" style={{ textAlign: "right" }}>
            <div style={{ color: "#0f172a", fontSize: 14.5, fontWeight: 600 }}>{user?.name}</div>
            <div style={{ color: "#64748b", fontSize: 11.5, textTransform: "uppercase", letterSpacing: 1 }}>{user?.role}</div>
          </div>
          <button data-tour="tour-help" onClick={tour.start} title="Take the guided tour" aria-label="Take the guided tour"
            style={{ width: 32, height: 32, borderRadius: '50%', background: '#e7f0fa', border: '1px solid #b9d0ea', color: '#1a5fa4', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
            ?
          </button>
          <button onClick={handleLogout} title={user?.name ? `Signed in as ${user.name}` : undefined}
            style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "8px 16px", color: "#b91c1c", fontSize: 14.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
            Logout
          </button>
        </div>
      </div>

      {/* Side panel (narrow screens) — slides in from the left */}
      {open && (
        <>
          <div className="mw-shade" onClick={() => setOpen(false)} aria-hidden="true"
            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 300, animation: "mwShadeIn .2s ease" }} />
          <aside id="mw-app-menu" className="mw-drawer" role="dialog" aria-modal="true" aria-label="Menu"
            style={{
              position: "fixed", top: 0, bottom: 0, left: 0, width: "min(300px, 84vw)", background: "#ffffff", zIndex: 301,
              boxShadow: "8px 0 30px rgba(15,23,42,0.25)", display: "flex", flexDirection: "column",
              animation: "mwDrawerIn .22s ease-out", overflowY: "auto",
            }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 12px 12px 16px", borderBottom: "1px solid #e2e8f0" }}>
              <Logo height={30} />
              <button onClick={() => setOpen(false)} aria-label="Close menu"
                style={{ width: 40, height: 40, borderRadius: 10, background: "#f1f5f9", border: "none", color: "#0f172a", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
              </button>
            </div>
            <div style={{ padding: "14px 16px", borderBottom: "1px solid #e2e8f0" }}>
              <div style={{ color: "#0f172a", fontSize: 16, fontWeight: 700 }}>{user?.name}</div>
              <div style={{ color: "#64748b", fontSize: 12, textTransform: "uppercase", letterSpacing: 1, marginTop: 2 }}>{user?.role}</div>
            </div>
            <nav aria-label="Pages" style={{ padding: "10px 10px", display: "grid", gap: 4 }}>
              {links.map(link => (
                <button key={link.path} onClick={() => navigate(link.path)} aria-current={isActive(link.path) ? 'page' : undefined}
                  style={{
                    textAlign: "left", background: isActive(link.path) ? "#1a5fa4" : "transparent", border: "none", borderRadius: 10,
                    padding: "13px 14px", color: isActive(link.path) ? "#ffffff" : "#0f172a", fontSize: 16.5, fontWeight: isActive(link.path) ? 700 : 500, cursor: "pointer",
                  }}>
                  {link.label}
                </button>
              ))}
            </nav>
            <div style={{ marginTop: "auto", padding: 16, display: "grid", gap: 8, borderTop: "1px solid #e2e8f0" }}>
              <button onClick={() => { setOpen(false); tour.start(); }}
                style={{ background: '#e7f0fa', border: '1px solid #b9d0ea', borderRadius: 10, padding: "12px", color: '#1a5fa4', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                Guided tour
              </button>
              <button onClick={handleLogout}
                style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "12px", color: "#b91c1c", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
                Logout
              </button>
            </div>
          </aside>
        </>
      )}
    </nav>
  );
}
